import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Send, MessageCircle, AlertTriangle, Image as ImageIcon, RotateCcw } from 'lucide-react';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { PhoneField } from '@/components/PhoneField';
import { Card, SectionHeader } from '@/components/Card';
import { MediaUpload } from '@/components/MediaUpload';
import { TemplatePreview } from '@/components/templates/TemplatePreview';
import { TemplateVariablesPanel } from '@/components/templates/TemplateVariablesPanel';
import { templatesService, Template, HeaderOverride, TemplateLocation } from '@/services/templates.service';

interface Props {
  open: boolean;
  template: Template | null;
  onClose: () => void;
}

/**
 * Send a single test WhatsApp message rendered from this template.
 * Uses POST /slighter_crm/templates/:uid/test-send. APPROVED only.
 */
export const TestSendModal: React.FC<Props> = ({ open, template, onClose }) => {
  const [phone, setPhone] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [override, setOverride] = useState<HeaderOverride>({});
  const [overrideEnabled, setOverrideEnabled] = useState(false);

  // Reset on open + seed values from previewSamples
  useEffect(() => {
    if (!open || !template) return;
    setPhone('');
    setValues({ ...(template.previewSamples ?? {}) });
    setOverride({});
    setOverrideEnabled(false);
  }, [open, template]);

  if (!open || !template) return null;

  const headerType = template.headerType ?? 'NONE';
  const supportsOverride =
    template.type !== 'CAROUSEL' &&
    (headerType === 'IMAGE' || headerType === 'VIDEO' || headerType === 'DOCUMENT' || headerType === 'LOCATION');

  const blockedReason: string | null =
    template.status !== 'APPROVED'
      ? `Template is ${template.status} — only APPROVED templates can be sent.`
      : template.waTemplateId?.startsWith('seed_')
        ? 'Demo placeholder ID — sync from Meta first.'
        : null;

  const buildOverridePayload = (): HeaderOverride | undefined => {
    if (!overrideEnabled || !supportsOverride) return undefined;
    const o: HeaderOverride = {};
    if (headerType === 'IMAGE' && override.imageUrl) o.imageUrl = override.imageUrl;
    if (headerType === 'VIDEO' && override.videoUrl) o.videoUrl = override.videoUrl;
    if (headerType === 'DOCUMENT' && override.documentUrl) {
      o.documentUrl = override.documentUrl;
      if (override.documentFilename) o.documentFilename = override.documentFilename;
    }
    if (headerType === 'LOCATION' && override.location) o.location = override.location;
    return Object.keys(o).length ? o : undefined;
  };

  const send = async () => {
    if (blockedReason) { toast.error(blockedReason); return; }
    if (!phone) { toast.error('Pick a country and enter the recipient phone'); return; }
    setBusy(true);
    try {
      const trimmed = Object.fromEntries(Object.entries(values).filter(([, v]) => v && v.trim() !== ''));
      const payload: { phone: string; variables?: Record<string, string>; headerOverride?: HeaderOverride } = {
        phone,
        variables: trimmed,
      };
      const ov = buildOverridePayload();
      if (ov) payload.headerOverride = ov;

      const r = await templatesService.testSend(template.templateUid, payload);
      if (r.ok) {
        onClose();
        trackDelivery(r.waMessageId);
      } else {
        toast.error(r.error ?? 'Send failed');
      }
    } catch { /* axios interceptor toasts */ } finally { setBusy(false); }
  };

  /**
   * Meta accepts messages synchronously (the wamid) but delivers or FAILS them
   * asynchronously via the status webhook — "Sent!" alone proves nothing.
   * Poll the stamped status for up to a minute and tell the user the truth.
   */
  const trackDelivery = (waMessageId?: string) => {
    if (!waMessageId) { toast.success('Accepted by Meta (no message id returned)'); return; }
    const toastId = toast.loading('Accepted by Meta — waiting for delivery…');
    const startedAt = Date.now();
    const poll = async () => {
      try {
        const s = await templatesService.sendStatus(waMessageId);
        if (s.deliveryStatus === 'failed') {
          toast.error(
            `Not delivered: ${s.failureReason ?? 'Meta reported a failure with no reason'}`,
            { id: toastId, duration: 15000 },
          );
          return;
        }
        if (s.deliveryStatus === 'delivered' || s.deliveryStatus === 'read') {
          toast.success('Delivered ✓', { id: toastId });
          return;
        }
      } catch { /* transient — keep polling */ }
      if (Date.now() - startedAt > 60_000) {
        toast(
          'Accepted by Meta, but no delivery receipt after 1 minute. If it never arrives, check the webhook configuration and the recipient number — marketing templates are also rate-limited per user by Meta.',
          { id: toastId, icon: '⏳', duration: 12000 },
        );
        return;
      }
      setTimeout(poll, 4000);
    };
    setTimeout(poll, 4000);
  };

  // Effective media URLs for the preview — use override values when enabled.
  const previewImageUrl = overrideEnabled && override.imageUrl ? override.imageUrl : template.headerImageUrl;
  const previewVideoUrl = overrideEnabled && override.videoUrl ? override.videoUrl : template.headerVideoUrl;
  const previewDocumentUrl = overrideEnabled && override.documentUrl ? override.documentUrl : template.headerDocumentUrl;
  const previewDocumentFilename = overrideEnabled && override.documentFilename ? override.documentFilename : template.headerDocumentFilename;
  const previewLocation = overrideEnabled && override.location ? override.location : template.headerLocation;

  return (
    <Modal open={open} onClose={onClose} title={`Test send — ${template.name}`} widthClass="max-w-3xl">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="space-y-4">
          {blockedReason && (
            <div className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning-soft px-3 py-2">
              <AlertTriangle size={14} className="mt-0.5 shrink-0 text-warning" />
              <p className="text-xs text-warning">{blockedReason}</p>
            </div>
          )}

          <Card>
            <SectionHeader title="Recipient" icon={<MessageCircle size={15} />} className="mb-3" />
            <FormField label="Phone number" required hint="The number that will receive this test message">
              <PhoneField
                value={phone}
                onChange={setPhone}
                placeholder="50 123 4567"
              />
            </FormField>
          </Card>

          {supportsOverride && (
            <Card>
              <SectionHeader title="Header media" icon={<ImageIcon size={15} />} className="!mb-3" />
              <label className="mb-3 flex cursor-pointer items-start gap-2 text-xs text-text-secondary">
                <input
                  type="checkbox"
                  checked={overrideEnabled}
                  onChange={(e) => {
                    setOverrideEnabled(e.target.checked);
                    if (!e.target.checked) setOverride({});
                  }}
                  className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded border-border accent-primary"
                />
                <span className="font-medium leading-snug">
                  Use a different {headerType.toLowerCase()} for this send
                </span>
              </label>
              {!overrideEnabled ? (
                <p className="rounded-md bg-bg-soft px-3 py-2 text-xs text-text-secondary">
                  Sending with the template's saved {headerType.toLowerCase()}.
                  Tick the box above to swap it for this send only — the template won't be modified.
                </p>
              ) : (
                <div className="space-y-2">
                  {headerType === 'IMAGE' && (
                    <MediaUpload
                      mode="image"
                      value={override.imageUrl ?? ''}
                      onChange={(url) => setOverride((s) => ({ ...s, imageUrl: url }))}
                      folder="template-overrides"
                    />
                  )}
                  {headerType === 'VIDEO' && (
                    <MediaUpload
                      mode="video"
                      value={override.videoUrl ?? ''}
                      onChange={(url) => setOverride((s) => ({ ...s, videoUrl: url }))}
                      folder="template-overrides"
                    />
                  )}
                  {headerType === 'DOCUMENT' && (
                    <>
                      <MediaUpload
                        mode="document"
                        value={override.documentUrl ?? ''}
                        onChange={(url, meta) =>
                          setOverride((s) => ({
                            ...s,
                            documentUrl: url,
                            documentFilename: s.documentFilename ?? meta?.name,
                          }))
                        }
                        filename={override.documentFilename}
                        onFilenameChange={(name) => setOverride((s) => ({ ...s, documentFilename: name }))}
                        folder="template-overrides"
                      />
                    </>
                  )}
                  {headerType === 'LOCATION' && (
                    <div className="grid grid-cols-2 gap-2">
                      <FormField label="Latitude">
                        <Input
                          type="number"
                          step="0.000001"
                          value={override.location?.latitude ?? ''}
                          onChange={(e) =>
                            setOverride((s) => ({
                              ...s,
                              location: { ...(s.location ?? {}), latitude: e.target.value === '' ? undefined : Number(e.target.value) },
                            }))
                          }
                        />
                      </FormField>
                      <FormField label="Longitude">
                        <Input
                          type="number"
                          step="0.000001"
                          value={override.location?.longitude ?? ''}
                          onChange={(e) =>
                            setOverride((s) => ({
                              ...s,
                              location: { ...(s.location ?? {}), longitude: e.target.value === '' ? undefined : Number(e.target.value) },
                            }))
                          }
                        />
                      </FormField>
                      <div className="col-span-2">
                        <FormField label="Name">
                          <Input
                            value={override.location?.name ?? ''}
                            onChange={(e) =>
                              setOverride((s) => ({
                                ...s,
                                location: { ...(s.location ?? {}), name: e.target.value },
                              }))
                            }
                          />
                        </FormField>
                      </div>
                      <div className="col-span-2">
                        <FormField label="Address">
                          <Input
                            value={override.location?.address ?? ''}
                            onChange={(e) =>
                              setOverride((s) => ({
                                ...s,
                                location: { ...(s.location ?? {}), address: e.target.value },
                              }))
                            }
                          />
                        </FormField>
                      </div>
                    </div>
                  )}
                  <button
                    type="button"
                    className="flex items-center gap-1 text-[11px] text-text-secondary hover:text-text-primary"
                    onClick={() => setOverride({})}
                  >
                    <RotateCcw size={11} /> Reset to template default
                  </button>
                </div>
              )}
            </Card>
          )}

          <Card>
            <SectionHeader title="Variables" icon={<Send size={15} />} className="mb-3" />
            <TemplateVariablesPanel
              template={template}
              values={values}
              onChange={setValues}
            />
          </Card>

          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button onClick={send} loading={busy} disabled={!!blockedReason || !phone} iconLeft={<Send size={13} />}>
              Send test
            </Button>
          </div>
        </div>

        {/* Preview */}
        <div className="hidden lg:block">
          <div className="sticky top-0 -mx-2 -my-2">
            <TemplatePreview
              headerType={template.type === 'CAROUSEL' ? 'NONE' : template.headerType}
              headerText={template.headerText}
              headerImageUrl={previewImageUrl}
              headerVideoUrl={previewVideoUrl}
              headerDocumentUrl={previewDocumentUrl}
              headerDocumentFilename={previewDocumentFilename}
              headerLocation={previewLocation as TemplateLocation | undefined}
              body={template.body}
              footer={template.type === 'CAROUSEL' ? undefined : template.footer}
              buttons={template.type === 'CAROUSEL' ? [] : template.buttons}
              cards={template.type === 'CAROUSEL' ? template.cards : undefined}
              samples={values}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};
